import { useMemo } from "react";
import { useOne } from "@refinedev/core";
import { useSession } from "hooks/useSession";
import type { VideoAspectRatio, VideoConfigDraft, VideoConfigOption } from "../types";
import { useGenerationFieldOptions } from "./useGenerationFieldOptions";

export type VideoConfigData = {
  durations: VideoConfigOption[];
  /**
   * Org-preference defaults used to preselect the video config fields. Any field
   * is omitted when the org has no stored preference for it — same field shape as
   * the form value, so the card can seed any field from it.
   */
  defaults: VideoConfigDraft;
};

export const useVideoConfigData = (): VideoConfigData => {
  const { durations } = useGenerationFieldOptions();
  const { currentOrganization, isAuthenticated } = useSession();
  const organizationId = currentOrganization?.id;

  const { data: preferencesData } = useOne({
    resource: "media/preferences",
    id: organizationId,
    queryOptions: {
      enabled: !!organizationId && !!isAuthenticated,
      // Don't refetch for each new page; the org settings page invalidates it
      // on update.
      refetchOnMount: false,
    },
  });
  // NOTE: only brand_kit_id/contact_id are in the preferences API today
  // (PreferenceResponse) — and those are general settings now, resolved in
  // `useGeneralConfigData`. The video fields below are not yet returned — they
  // resolve to undefined until the backend starts sending them, at which point
  // the defaults flow through.
  const preferences = preferencesData?.data as
    | {
        /** Seconds — matches `VideoGenerationSettings.duration`. */
        duration?: number | null;
        show_avatar?: boolean | null;
        background_music?: boolean | null;
        subtitles?: boolean | null;
        aspect_ratio?: VideoAspectRatio | null;
      }
    | undefined;

  return {
    durations,
    // Coalesce the API's null (no stored preference) to undefined so each field
    // matches the optional VideoConfigDraft shape and the card treats it as unset.
    defaults: useMemo<VideoConfigDraft>(
      () => ({
        generation_settings: {
          duration: preferences?.duration ?? undefined,
          with_avatar: preferences?.show_avatar ?? undefined,
          auto_select_music: preferences?.background_music ?? undefined,
          aspect_ratio: preferences?.aspect_ratio ?? undefined,
        },
        // Hidden for now — waiting for backend confirmation that subtitles are
        // supported. Re-enable with the card's toggle.
        // subtitles: preferences?.subtitles ?? undefined,
      }),
      [preferences]
    ),
  };
};

export default useVideoConfigData;
